import { QuotesService } from './../../services/quotes.service';
import { Quote, QuoteOption } from './../../models/quote.model';
import { ErrorResponse } from './../../models/error.model';
import { PageEvent } from '@angular/material/paginator';
import { UtilsService } from 'src/app/services/utils.service';
import { DataTableColumn, PaginatorStatus, SortConfiguration } from './../generics/data-table/data-table.component';
import { CustomTableFilter } from '../generics/table-filter/table-filter.component';
import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { UserType } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { BrokersService } from 'src/app/services/brokers.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { FastQuoteDialogComponent } from '../../dialogs/fast-quote-dialog/fast-quote-dialog.component';

@Component({
  selector: 'app-quotes',
  templateUrl: './quotes.component.html',
  styleUrls: ['./quotes.component.scss']
})
export class QuotesComponent implements OnInit {

    @ViewChild('buttonsColumnTemplate', { static: true })
    buttonsColumnTeplate: TemplateRef<any>;

    paginatorStatus: PaginatorStatus = {
        itemsPerPage: 10,
        page: 0,
        totalElementCount: 0
    };

    columns: DataTableColumn[] = [
        { name: 'ID', prop: 'id', sortable: true, width: '70px' },
        { name: 'Fecha', prop: 'fechaCreacion', sortable: true, type: 'datetime' },
        { name: 'Alquiler', prop: 'alquiler', sortable: true, type: 'money' },
        { name: 'Expensas', prop: 'expensas', sortable: true, type: 'money' },
        { name: 'Valor 1ra cuota', prop: 'opciones', parseValue: (value: QuoteOption[]) => '$' + this.utilsService.beautifyNumber(value.find(option => option.cuotas === 1)?.montoTotal ?? 0, 2) },
        { name: 'Tipo', prop: 'tipo', sortable: true },
        { name: 'Propiedad', prop: 'propiedad.direccion', type: 'resumed-address' },
        { name: 'Agente', parseValue: (value: Quote) => value.nombreAgente ?? '-' },
        { name: '', width: '140px', cellClass: 'align-right-cell' }
    ];

    sortConfiguration?: SortConfiguration;

    quotes: Quote[];
    filters: CustomTableFilter[] = [];
    filterValues: any = {};

    userType?: UserType;

    constructor(
        private quotesService: QuotesService,
        private authService: AuthService,
        private utilsService: UtilsService,
        private brokersService: BrokersService,
        private dialog: MatDialog
    ) {}

    abrirCotizacionRapida() {
        this.dialog.open(FastQuoteDialogComponent, { width: '900px' })
            .afterClosed().subscribe(() => this.getQuotes());
    }

    ngOnInit(): void {
        this.columns.find(col => col.name === '').template = this.buttonsColumnTeplate;
        this.userType = this.authService.getUser()?.tipo;
        this.filters = this.buildFilters();
        this.getQuotes();
        if (this.userType !== 'AGENTE') {
            this.columns[this.columns.length - 1].width = '70px';
        }
    }

    private buildFilters(): CustomTableFilter[] {
        const filters: CustomTableFilter[] = [
            { key: 'id', description: 'ID', type: 'NUMBER' },
        ];
        if (this.userType !== 'AGENTE') {
            filters.push({
                key: 'id-agente',
                description: 'Agente',
                type: 'AUTOCOMPLETE',
                searchFn: (query: string) => forkJoin([
                    this.brokersService.getBrokers(0, 20, [], { nombre: query }),
                    this.brokersService.getBrokers(0, 20, [], { apellido: query })
                ]).pipe(map(([r1, r2]) => {
                    const all = [...(r1.body ?? []), ...(r2.body ?? [])];
                    const unique = all.filter((b, i, arr) => arr.findIndex(x => x.id === b.id) === i);
                    return unique.map(b => ({ codigo: String(b.id), descripcion: `${b.nombre} ${b.apellido}` }));
                }))
            });
        }
        filters.push(
            { key: 'propiedad-codigo',     description: 'Código de propiedad',        type: 'STRING' },
            { key: 'propiedad-direccion',  description: 'Dirección',                  type: 'STRING' },
            { key: 'fecha-desde',          description: 'Fecha desde',                type: 'DATE' },
            { key: 'fecha-hasta',          description: 'Fecha hasta',                type: 'DATE' },
            { key: 'alquiler-desde',       description: 'Alquiler desde $',           type: 'NUMBER' },
            { key: 'alquiler-hasta',       description: 'Alquiler hasta $',           type: 'NUMBER' },
            { key: 'expensas-desde',       description: 'Expensas desde $',           type: 'NUMBER' },
            { key: 'expensas-hasta',       description: 'Expensas hasta $',           type: 'NUMBER' },            
            { key: 'propietario-nombre',   description: 'Nombre del propietario',     type: 'STRING' },
            { key: 'propietario-apellido', description: 'Apellido del propietario',   type: 'STRING' },
            { key: 'propietario-dni',      description: 'DNI del propietario',        type: 'DIGITS' }
        );
        return filters;
    }

    getQuotes() {
        this.quotesService.getQuotes(
            this.paginatorStatus.page,
            this.paginatorStatus.itemsPerPage,
            this.sortConfiguration ? [this.sortConfiguration] : [],
            this.filterValues
        ).subscribe(response => {
            this.paginatorStatus.totalElementCount = parseInt(response.headers.get('pagination-total-count') ?? '0', 10);
            this.quotes = response.body;
        }, (error: ErrorResponse) => this.utilsService.alert('Error al obtener cotizaciones', error.error?.descripcion));
    }

    setFilterValues(values: any) {
        this.filterValues = values;
        this.paginatorStatus.page = 0;
        this.getQuotes();
    }

    updateQuotes(event: PageEvent) {
        this.paginatorStatus.itemsPerPage = event.pageSize;
        this.paginatorStatus.page = event.pageIndex;
        this.getQuotes();
    }

    updateSort(event: SortConfiguration) {
        this.sortConfiguration = event;
        this.getQuotes();
    }

    deleteQuote(quote: Quote) {
        const dialog = this.utilsService.confirm('¿Está seguro?', 'Esta acción no se puede deshacer.');
        dialog.afterClosed().subscribe(result => {
            if (result) {
                this.quotesService.deleteQuote(quote.id).subscribe(_ => {
                    this.utilsService.alert('Cotización borrada correctamente', `Se ha eliminado la cotización número ${quote.id} de la plataforma.`);
                    this.getQuotes();
                }, (error: ErrorResponse) => this.utilsService.alert('Error al borrar cotización', error.error?.descripcion));
            }
        });
    }

}
